<template>
  <AisInfiniteHits class="FilterResults">
    <template #default="{ items, refineNext, isLastPage, sendEvent }">
      <ul
        :class="bem('list', {
          lt3: (items.length - props.banners.length) < 3,
          lt6: (items.length - props.banners.length) < 6,
          lt9: (items.length - props.banners.length) < 9,
          [`desktop-items-to-hide--${items.length % 3 }`]: !isLastPage,
          [`mobile-items-to-hide--${items.length % 2 }`]: !isLastPage
        })"
      >
        <li
          v-for="(item, index) in items"
          v-show="item.sku || (item.type === 'banner' && item.banners.length)"
          :key="item.objectID"
          :class="bem('list-item', {
            isBanner: item.type === 'banner',
            [`pos-${positioning}-${item.bannerIndex}`]: item.type === 'banner'
          })"
          :style="item.type !== 'banner' ? { order: index } : {}"
        >
          <FilterBanner
            v-if="item.type === 'banner' && item.banners.length"
            :banners="item.banners"
          />
          <LiteProductCard
            v-if="item.sku"
            :product="item"
            :is-filter-result="true"
            @click="sendEvent('click', item, 'Hit Clicked', {
              positions: [ index + 1 ],
              queryId: item.queryId
            });"
          />
        </li>
      </ul>
      <LoadMoreResults
        v-if="!isLastPage"
        @show-more="refineNext"
      />
    </template>
  </AisInfiniteHits>
</template>

<script setup>
const props = defineProps({
  banners: {
    type: Object,
    required: true,
  },
  positioning: {
    type: String,
    default: 'random'
  }
})

const bem = useBEM('FilterResults')
</script>

<style lang="postcss" scoped>
.FilterResults {
  @apply grid gap-y-10 relative;

  grid-template-areas: 'results' 'results-footer';
  grid-template-columns: 1fr;
}

.FilterResults__list {
  @apply grid gap-x-2 md:gap-x-4 md:gap-y-10 gap-y-8;

  grid-area: results;
  grid-template-columns: repeat(auto-fill, minmax(160px, 1fr));
  grid-auto-rows: 1fr;

  /* Limit to three rows */
  grid-template-rows: repeat(3, auto);

  @media (min-width: 520px) {
    grid-template-columns: repeat(auto-fill, minmax(200px, 1fr));
  }

  @media (min-width: 640px) {
    grid-template-columns: repeat(auto-fill, minmax(250px, 1fr));
  }

  @media (min-width: 790px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }

  @screen lg {
    grid-template-columns: repeat(auto-fill, minmax(300px, 1fr));
  }

  @media (min-width: 1384px) {
    grid-template-columns: repeat(auto-fill, minmax(350px, 1fr));
  }
}
,
.FilterResults__list--desktop-items-to-hide-1 {
  /** Breakpoint for 3 column grid taking effect */
  @media (min-width: 1076px) {
    .FilterResults__list-item:last-child {
      display: none;
    }
  }
}

.FilterResults__list--mobile-items-to-hide-1 {
  /** Breakpoint for 2 column grid taking effect */
  @media (max-width: 1075px) {
    .FilterResults__list-item:last-child {
      display: none;
    }
  }
}

.FilterResults__list--desktop-items-to-hide-2 {
  /** Breakpoint for 3 column grid taking effect */
  @media (min-width: 1076px) {
    .FilterResults__list-item:nth-last-child(-n + 2) {
      display: none;
    }
  }
}

.FilterResults__footer {
  @apply flex justify-center;

  grid-area: results-footer;
}

.FilterResults__list-item {
  &--pos-inline-0,
  &--pos-inline-1,
  &--pos-inline-2 {
    grid-column-start: 1;

    @screen md {
      grid-column-start: 2;
    }
  }

  &--pos-inline-0 {
    grid-row-start: 1;
  }

  &--pos-inline-1 {
    grid-row-start: 2;
  }

  &--pos-inline-2 {
    grid-row-start: 3;
  }

  &--pos-random-0 {
    /**
      First row last position on 2 column grid
    */
    order: 2;

    @media (min-width: 1076px) {
      /**
        First row last position on 3 column grid
      */
      order: 3;
    }
  }

  &--pos-random-1 {
    /**
      Third row first position on 2 column grid
    */
    order: 5;

    @media (min-width: 1076px) {
      /**
        Third row first position on 3 column grid
      */
      order: 7;
    }
  }

  &--pos-random-2 {
    /**
      Fifth row last position on 2 column grid
    */
    order: 10;

    @media (min-width: 1076px) {
    /**
      Fifth row middle position on 3 column grid
    */
      order: 14;
    }
  }

  &--pos-random-1,
  &--pos-inline-1 {
    .FilterResults__list--lt3 &,
    .FilterResults__list--lt6 & {
      @apply hidden;
    }
  }

  &--pos-random-2,
  &--pos-inline-2 {
    .FilterResults__list--lt6 & {
      @apply hidden;
    }
  }

  /**
    This stops filter banners collapsing in height when they appear beside each
    other in a two grid format.
  */
  &--is-banner + &--is-banner {
    @media (max-width: 1075px) {
        @apply aspect-[200/300];
    }
  }
}
</style>
